import classNames from "classnames";
import React, { FC } from "react";
import { Calendar, Month } from "../../../hooks/use-calendar/use-calendar";
import { Button } from "../../button";
import { IconButton } from "../../button/icon-button";
import { IconNavArrowLeft, IconNavArrowRight } from "../../icons";
import { MaskedInputProps } from "../../input";
import { CSS_CLASSES } from "../date-time-input-css-classes";

export interface MonthOwnProps {
  month: Month;
  showPrevNavigation: boolean;
  showNextNavigation: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  showOutsideMonthDates?: boolean;
  allowSelectOutsideMonthDates?: boolean;
  color?: MaskedInputProps["color"];
}

export type CalendarToMonthProps = Pick<
  Calendar,
  | "getDayProps"
  | "getGridProps"
  | "getPreviousMonthProps"
  | "getNextMonthProps"
  | "getPreviousYearProps"
  | "getNextYearProps"
>;

export type MonthProps = MonthOwnProps & CalendarToMonthProps;

export const MonthComponent: FC<MonthProps> = ({
  month,
  showPrevNavigation,
  showNextNavigation,
  getDayProps,
  getGridProps,
  getPreviousMonthProps,
  getNextMonthProps,
  selectsStart,
  selectsEnd,
  showOutsideMonthDates = false,
  allowSelectOutsideMonthDates = false,
  color,
}) => {
  return (
    <div className={CSS_CLASSES.DATE_TIME_INPUT_MONTH}>
      <div className={CSS_CLASSES.DATE_TIME_INPUT_MONTH_HEADER}>
        {showPrevNavigation && (
          <IconButton
            icon={<IconNavArrowLeft />}
            variant="ghost"
            size="small"
            className="mx-2"
            color={color}
            {...getPreviousMonthProps()}
          />
        )}
        <div className={CSS_CLASSES.DATE_TIME_INPUT_MONTH_TITLE}>
          {month.monthName} {month.year}
        </div>
        {showNextNavigation && (
          <IconButton
            icon={<IconNavArrowRight />}
            variant="ghost"
            size="small"
            className="mx-2"
            color={color}
            {...getNextMonthProps()}
          />
        )}
      </div>

      <table className={CSS_CLASSES.DATE_TIME_INPUT_WEEKS} {...getGridProps()}>
        <thead>
          <tr>
            <th scope="col" abbr="Sunday">
              S
            </th>
            <th scope="col" abbr="Monday">
              M
            </th>
            <th scope="col" abbr="Tuesday">
              T
            </th>
            <th scope="col" abbr="Wednesday">
              W
            </th>
            <th scope="col" abbr="Thursday">
              T
            </th>
            <th scope="col" abbr="Friday">
              F
            </th>
            <th scope="col" abbr="Saturday">
              S
            </th>
          </tr>
        </thead>
        <tbody>
          {month.weeks.map((week) => {
            return (
              <tr className={CSS_CLASSES.DATE_TIME_INPUT_WEEK} key={`${week.weekNumber}`}>
                {week.days.map((day) => {
                  let selectionRangeStart = false;
                  let selectionRangeEnd = false;
                  let selectionInRange = false;
                  if (selectsStart || selectsEnd) {
                    selectionRangeStart =
                      (selectsEnd && day.rangeStart) ||
                      (selectsStart && ((day.rangeStart && !day.selectionInRange) || day.selectionRangeStart));
                    selectionRangeEnd =
                      (selectsStart && day.rangeEnd) ||
                      (selectsEnd && ((day.rangeEnd && !day.selectionInRange) || day.selectionRangeEnd));
                    selectionInRange =
                      (selectsEnd && (day.inRange || day.selectionInRange) && !day.rangeStart) ||
                      (selectsStart && (day.inRange || day.selectionInRange) && !day.rangeEnd);
                  }
                  let classes = classNames({
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY]: true,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_OUTSIDE_OF_MONTH]: !day.belongToMonth,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_PRE_SELECTED]: day.belongToMonth && day.preSelected,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_SELECTED]: day.selected,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_DISABLED]: day.disabled,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_RANGE_START]: selectionRangeStart,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_IN_RANGE]: selectionInRange,
                    [CSS_CLASSES.DATE_TIME_INPUT_DAY_RANGE_END]: selectionRangeEnd,
                  });
                  return (
                    <td key={`${week.weekNumber}-${day.dayNumber}`} className={classes}>
                      {(day.belongToMonth || showOutsideMonthDates) && (
                        <Button
                          width="full"
                          labelAlign="center"
                          variant="ghost"
                          size="small"
                          color="gray"
                          label={day.dayNumber}
                          styleOverride="normal"
                          disabled={day.disabled || (!day.belongToMonth && !allowSelectOutsideMonthDates)}
                          {...getDayProps(day)}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
